/**
 * RoleSelector - Radio-like selector for choosing between roles
 *
 * Dashboard3 styled role selector with visual feedback.
 *
 * @example
 * ```tsx
 * <RoleSelector
 *   role={role}
 *   setRole={setRole}
 *   options={[
 *     { label: "Parent", value: "parent" },
 *     { label: "Student", value: "student" },
 *   ]}
 * />
 * ```
 */

"use client";

import { Box, Flex } from "@chakra-ui/react";
import { useThemeTokens } from "@/lib/theme/tokens";
import { Text } from "@/components/ui";

export interface RoleOption<T extends string = string> {
  label: string;
  value: T;
}

export interface RoleSelectorProps<T extends string = string> {
  /**
   * Current selected role
   */
  role: T;
  /**
   * Change handler
   */
  setRole: (role: T) => void;
  /**
   * Available role options
   */
  options: RoleOption<T>[];
  /**
   * Breakpoint above which underline tabs replace segmented pills. Default md.
   */
  segmentedUntil?: "md" | "lg";
}

export const RoleSelector = <T extends string = string>({
  role,
  setRole,
  options,
  segmentedUntil = "md",
}: RoleSelectorProps<T>) => {
  const theme = useThemeTokens();
  const desktopBreakpoint = segmentedUntil;

  const mobileTrackBg =
    theme.mode === "dark"
      ? "rgba(255,255,255,0.05)"
      : "rgba(11,13,16,0.04)";

  return (
    <Flex
      w="100%"
      justify="flex-start"
      gap={{ base: 1, [desktopBreakpoint]: "24px" }}
      p={{ base: 1, [desktopBreakpoint]: 0 }}
      borderRadius={{ base: "12px", [desktopBreakpoint]: 0 }}
      borderWidth={{ base: "1px", [desktopBreakpoint]: 0 }}
      borderColor={{ base: theme.borderGlass.default, [desktopBreakpoint]: "transparent" }}
    >
      {options.map((option) => {
        const isSelected = role === option.value;

        return (
          <Flex
            key={option.value}
            align="center"
            justify={{ base: "center", [desktopBreakpoint]: "flex-start" }}
            cursor="pointer"
            direction="column"
            onClick={() => setRole(option.value)}
            transition="all 0.18s ease-out"
            flex={{ base: 1, [desktopBreakpoint]: "none" }}
            minW={{ base: 0, [desktopBreakpoint]: "auto" }}
            py={{ base: 2.5, [desktopBreakpoint]: 0 }}
            px={{ base: 1.5, [desktopBreakpoint]: 0 }}
            borderRadius={{ base: "8px", [desktopBreakpoint]: 0 }}
            bg={{
              base: isSelected ? theme.accent.primaryBg : "transparent",
              [desktopBreakpoint]: "transparent",
            }}
          >
            <Text
              fontSize={{ base: "sm", [desktopBreakpoint]: "lg" }}
              fontWeight={{ base: isSelected ? 600 : 500, [desktopBreakpoint]: 400 }}
              textAlign={{ base: "center", [desktopBreakpoint]: "left" }}
              lineHeight={{ base: "1.25", [desktopBreakpoint]: "1.5" }}
              color={isSelected ? theme.accent.primary : theme.text.secondary}
              sx={{
                "@media (hover: hover)": {
                  "&:hover": {
                    color: theme.accent.primary,
                  },
                },
              }}
            >
              {option.label}
            </Text>
            {isSelected && (
              <Box
                display={{ base: "none", [desktopBreakpoint]: "block" }}
                h="3px"
                w="100%"
                bg={theme.accent.primary}
                borderRadius="30px"
                mt={1}
                transition="all 0.18s ease-out"
              />
            )}
          </Flex>
        );
      })}
    </Flex>
  );
};
